import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ChevronDown, Droplets } from "lucide-react";
import SEO from "../../components/site/SEO";
import { T } from "../../lib/testIds";

const FAQS = [
  {
    id: "siding",
    q: "Will pressure washing damage my siding?",
    a: "No. Wonder Washers uses a low-pressure soft-wash system for siding — professional cleaning solutions do the work, then a gentle rinse at a fraction of rental-machine pressure. That means no stripped paint, no water behind the walls, and no etched surfaces on vinyl, fiberglass, painted, or stucco exteriors.",
  },
  {
    id: "roof",
    q: "Is it safe to pressure wash my roof?",
    a: "Yes — because we don't pressure wash it. Roofs are never blasted. Roof cleaning is soft-wash only, killing moss, algae, and lichen at the root with no shingle granule loss and no shortened roof life. Safe for asphalt shingle, tile, and metal.",
  },
  {
    id: "cedar",
    q: "Can you clean a cedar deck without damaging it?",
    a: "Absolutely. Our fully adjustable system is dialed down to wood-safe pressure and paired with wide-fan nozzles. We clean with the grain — no fuzzing, no gouging, no wand marks. It's perfect prep for staining, and it works on cedar, composite, and painted decks.",
  },
  {
    id: "water",
    q: "Do you use my home's water?",
    a: "We bring our own. Every rig runs on a 330-gallon onboard water tank, so your job never depends on your spigot, your well, or your water pressure — and there's no unexpected draw on your utility bill either.",
  },
  {
    id: "rental",
    q: "What makes professional equipment different from a rental?",
    a: "Three things. First: hot water and wet-steam operation up to 248°F cuts through grease, gum, oil, and organic growth that cold rental machines can't touch. Second: a belt-driven commercial pump with pump-protection, plus fully adjustable pressure so we match every surface. Third — and most importantly — the expertise to know when NOT to use pressure. A rental machine is one setting. A pro rig is every setting, dialed to the job.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState("siding");
  return (
    <>
      <SEO
        title="FAQ — Wonder Washers Pressure Washing"
        description="Answers about safe pressure washing for siding, roofs, and cedar decks in Oregon. Soft-wash, adjustable pressure, and 330-gallon onboard water tank explained."
        canonical="[BOOKING_URL]/faq"
      />
      <section className="bg-[color:var(--ww-navy)] text-white py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-gold)] uppercase mb-3">FAQ</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl leading-none">The questions our neighbors actually ask.</h1>
          <p className="mt-5 text-slate-300 max-w-2xl">Straight answers about safety, equipment, and what "commercial-grade equipment, residential-grade care" actually means when it's your home.</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto py-16 md:py-20 px-6" data-testid={T.faq.list}>
        <div className="space-y-3">
          {FAQS.map((f) => {
            const isOpen = open === f.id;
            return (
              <div key={f.id} data-testid={T.faq.item(f.id)} className="ww-card overflow-hidden">
                <button
                  data-testid={T.faq.toggle(f.id)}
                  onClick={() => setOpen(isOpen ? "" : f.id)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left group"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-3">
                    <ShieldCheck className={`h-5 w-5 shrink-0 ${isOpen ? "text-[color:var(--ww-red)]" : "text-[color:var(--ww-pressure-blue)]"}`} />
                    <span className="font-subhead uppercase tracking-wide text-[color:var(--ww-navy)] text-base md:text-lg">{f.q}</span>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180 text-[color:var(--ww-red)]" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 md:px-6 pb-6 -mt-1">
                    <div className="border-l-4 border-[color:var(--ww-red)] pl-5">
                      <p className="text-slate-700 leading-relaxed" data-testid={T.faq.answer(f.id)}>{f.a}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
      <section className="max-w-4xl mx-auto py-8 pb-24 px-6 text-center">
        <Droplets className="h-8 w-8 text-[color:var(--ww-pressure-blue)] mx-auto" />
        <h2 className="mt-4 font-display uppercase text-4xl text-[color:var(--ww-navy)] leading-tight">Have a question we didn't cover?</h2>
        <p className="mt-3 text-slate-600 max-w-lg mx-auto">Text or call [PHONE], or shoot us a note — a real Wonder Washers team member gets back within the hour.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/contact" className="ww-btn-primary">Ask us anything</Link>
          <Link to="/book" className="ww-btn-outline">Book online</Link>
        </div>
      </section>
    </>
  );
}
